import React, { useRef, useState } from "react";
import { PlusIcon, TrashIcon, CloudArrowUpIcon } from "@heroicons/react/24/outline";
import { ContentBlock, MindmapNode, newId } from "../../../types/courseBlocks";
import { uploadImageToCloudinary } from "../../../services/cloudinaryService";
import DynamicMarkdownEditor from "../../DynamicMarkdownEditor";
import CanvasSandbox from "../../course/CanvasSandbox";

interface BlockFormProps<T extends ContentBlock> {
  block: T;
  onChange: (block: T) => void;
}

const inputClass =
  "w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm p-2";
const labelClass = "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1";

export const TextBlockForm: React.FC<BlockFormProps<ContentBlock & { type: "text" }>> = ({ block, onChange }) => (
  <DynamicMarkdownEditor value={block.markdown} onChange={(v) => onChange({ ...block, markdown: v })} />
);

export const CodeBlockForm: React.FC<BlockFormProps<ContentBlock & { type: "code" }>> = ({ block, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Language</label>
      <input
        className={inputClass}
        value={block.language || ""}
        onChange={(e) => onChange({ ...block, language: e.target.value })}
      />
    </div>
    <div>
      <label className={labelClass}>Filename (optional)</label>
      <input
        className={inputClass}
        value={block.filename || ""}
        onChange={(e) => onChange({ ...block, filename: e.target.value })}
      />
    </div>
    <div>
      <label className={labelClass}>Code</label>
      <textarea
        className={`${inputClass} h-32 font-mono`}
        value={block.code}
        onChange={(e) => onChange({ ...block, code: e.target.value })}
      />
    </div>
  </div>
);

export const Canvas2DBlockForm: React.FC<BlockFormProps<ContentBlock & { type: "canvas2d" }>> = ({
  block,
  onChange,
}) => (
  <div className="space-y-2">
    <p className="text-xs text-gray-500">
      Define a <code>function draw(ctx, t, width, height)</code> — called every animation frame with the 2D
      context, elapsed seconds, and canvas size. Runs sandboxed; it can't access the rest of the page.
    </p>
    <textarea
      className={`${inputClass} h-40 font-mono`}
      value={block.code}
      onChange={(e) => onChange({ ...block, code: e.target.value })}
    />
    <CanvasSandbox code={block.code} />
  </div>
);

const ImageUploadField: React.FC<{ url: string; onUploaded: (url: string) => void }> = ({ url, onUploaded }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const uploadedUrl = await uploadImageToCloudinary(file);
      onUploaded(uploadedUrl);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      {url && <img src={url} alt="" className="max-h-40 rounded-md border border-gray-200 dark:border-gray-700" />}
      <div className="flex items-center gap-2">
        <input
          className={inputClass}
          placeholder="Image URL (or upload below)"
          value={url}
          onChange={(e) => onUploaded(e.target.value)}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
        >
          <CloudArrowUpIcon className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
};

export const ImageBlockForm: React.FC<BlockFormProps<ContentBlock & { type: "image" }>> = ({ block, onChange }) => (
  <div className="space-y-2">
    <ImageUploadField url={block.url} onUploaded={(url) => onChange({ ...block, url })} />
    <input
      className={inputClass}
      placeholder="Alt text"
      value={block.alt || ""}
      onChange={(e) => onChange({ ...block, alt: e.target.value })}
    />
    <input
      className={inputClass}
      placeholder="Caption (optional)"
      value={block.caption || ""}
      onChange={(e) => onChange({ ...block, caption: e.target.value })}
    />
    <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
      <input
        type="checkbox"
        checked={!!block.isPlaceholder}
        onChange={(e) => onChange({ ...block, isPlaceholder: e.target.checked })}
      />
      Placeholder image
    </label>
  </div>
);

export const SlidesBlockForm: React.FC<BlockFormProps<ContentBlock & { type: "slides" }>> = ({ block, onChange }) => (
  <div className="space-y-3">
    {block.slides.map((slide, index) => (
      <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-md p-2 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Slide {index + 1}</span>
          <button
            type="button"
            onClick={() => onChange({ ...block, slides: block.slides.filter((_, i) => i !== index) })}
            className="text-rose-500"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
        <ImageUploadField
          url={slide.imageUrl}
          onUploaded={(imageUrl) =>
            onChange({
              ...block,
              slides: block.slides.map((s, i) => (i === index ? { ...s, imageUrl } : s)),
            })
          }
        />
        <input
          className={inputClass}
          placeholder="Caption (optional)"
          value={slide.caption || ""}
          onChange={(e) =>
            onChange({
              ...block,
              slides: block.slides.map((s, i) => (i === index ? { ...s, caption: e.target.value } : s)),
            })
          }
        />
      </div>
    ))}
    <button
      type="button"
      onClick={() => onChange({ ...block, slides: [...block.slides, { imageUrl: "" }] })}
      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600"
    >
      <PlusIcon className="h-4 w-4" /> Add slide
    </button>
  </div>
);

export const FlashcardsBlockForm: React.FC<BlockFormProps<ContentBlock & { type: "flashcards" }>> = ({
  block,
  onChange,
}) => (
  <div className="space-y-2">
    {block.cards.map((card, index) => (
      <div key={index} className="flex gap-2 items-start">
        <input
          className={inputClass}
          placeholder="Term"
          value={card.term}
          onChange={(e) =>
            onChange({ ...block, cards: block.cards.map((c, i) => (i === index ? { ...c, term: e.target.value } : c)) })
          }
        />
        <input
          className={inputClass}
          placeholder="Definition"
          value={card.definition}
          onChange={(e) =>
            onChange({
              ...block,
              cards: block.cards.map((c, i) => (i === index ? { ...c, definition: e.target.value } : c)),
            })
          }
        />
        <button
          type="button"
          onClick={() => onChange({ ...block, cards: block.cards.filter((_, i) => i !== index) })}
          className="text-rose-500 mt-2"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    ))}
    <button
      type="button"
      onClick={() => onChange({ ...block, cards: [...block.cards, { term: "", definition: "" }] })}
      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600"
    >
      <PlusIcon className="h-4 w-4" /> Add card
    </button>
  </div>
);

export const QuizBlockForm: React.FC<BlockFormProps<ContentBlock & { type: "quiz" }>> = ({ block, onChange }) => (
  <div className="space-y-2">
    {block.questions.map((q, index) => (
      <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-md p-2 space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Question {index + 1}</span>
          <button
            type="button"
            onClick={() => onChange({ ...block, questions: block.questions.filter((_, i) => i !== index) })}
            className="text-rose-500"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
        <input
          className={inputClass}
          placeholder="Question"
          value={q.question}
          onChange={(e) =>
            onChange({
              ...block,
              questions: block.questions.map((item, i) => (i === index ? { ...item, question: e.target.value } : item)),
            })
          }
        />
        <input
          className={inputClass}
          placeholder="Answer"
          value={q.answer}
          onChange={(e) =>
            onChange({
              ...block,
              questions: block.questions.map((item, i) => (i === index ? { ...item, answer: e.target.value } : item)),
            })
          }
        />
      </div>
    ))}
    <button
      type="button"
      onClick={() => onChange({ ...block, questions: [...block.questions, { question: "", answer: "" }] })}
      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600"
    >
      <PlusIcon className="h-4 w-4" /> Add question
    </button>
  </div>
);

interface MindmapNodeFieldsProps {
  node: MindmapNode;
  depth: number;
  onChange: (node: MindmapNode) => void;
  onRemove: () => void;
}

const MindmapNodeFields: React.FC<MindmapNodeFieldsProps> = ({ node, depth, onChange, onRemove }) => {
  const children = node.children || [];

  const addChild = () => {
    onChange({ ...node, children: [...children, { id: newId(), label: "" }] });
  };

  const updateChild = (index: number, updated: MindmapNode) => {
    onChange({ ...node, children: children.map((c, i) => (i === index ? updated : c)) });
  };

  const removeChild = (index: number) => {
    onChange({ ...node, children: children.filter((_, i) => i !== index) });
  };

  return (
    <div style={{ marginLeft: depth * 18 }} className="space-y-1">
      <div className="flex items-center gap-2">
        <input
          className={inputClass}
          placeholder="Item label"
          value={node.label}
          onChange={(e) => onChange({ ...node, label: e.target.value })}
        />
        <button
          type="button"
          onClick={addChild}
          title="Add sub-item"
          className="p-2 rounded-md border border-gray-300 dark:border-gray-600"
        >
          <PlusIcon className="h-4 w-4" />
        </button>
        <button type="button" onClick={onRemove} className="text-rose-500">
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
      {children.length > 0 && (
        <div className="space-y-1 border-l border-gray-200 dark:border-gray-700 pl-2">
          {children.map((child, index) => (
            <MindmapNodeFields
              key={child.id}
              node={child}
              depth={depth + 1}
              onChange={(updated) => updateChild(index, updated)}
              onRemove={() => removeChild(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const MindmapBlockForm: React.FC<BlockFormProps<ContentBlock & { type: "mindmap" }>> = ({
  block,
  onChange,
}) => {
  const addRootNode = () => {
    onChange({ ...block, nodes: [...block.nodes, { id: newId(), label: "" }] });
  };

  const updateRootNode = (index: number, updated: MindmapNode) => {
    onChange({ ...block, nodes: block.nodes.map((n, i) => (i === index ? updated : n)) });
  };

  const removeRootNode = (index: number) => {
    onChange({ ...block, nodes: block.nodes.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-2">
      <input
        className={inputClass}
        placeholder="Central label"
        value={block.centralLabel}
        onChange={(e) => onChange({ ...block, centralLabel: e.target.value })}
      />
      <p className="text-xs text-gray-500">
        Each item can have sub-items, which can have their own sub-items, and so on — click "+" on any item to nest
        under it. On the public page, readers click the arrow to expand one level at a time.
      </p>
      {block.nodes.map((node, index) => (
        <MindmapNodeFields
          key={node.id}
          node={node}
          depth={0}
          onChange={(updated) => updateRootNode(index, updated)}
          onRemove={() => removeRootNode(index)}
        />
      ))}
      <button
        type="button"
        onClick={addRootNode}
        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600"
      >
        <PlusIcon className="h-4 w-4" /> Add top-level item
      </button>
    </div>
  );
};

export const VideoBlockForm: React.FC<BlockFormProps<ContentBlock & { type: "video" }>> = ({ block, onChange }) => (
  <div className="space-y-2">
    <input
      className={inputClass}
      placeholder="Title"
      value={block.title}
      onChange={(e) => onChange({ ...block, title: e.target.value })}
    />
    <input
      className={inputClass}
      placeholder="YouTube video ID"
      value={block.youtubeVideoId || ""}
      onChange={(e) => onChange({ ...block, youtubeVideoId: e.target.value || undefined })}
    />
    <input
      className={inputClass}
      placeholder="Direct video URL (used if no YouTube ID)"
      value={block.videoUrl || ""}
      onChange={(e) => onChange({ ...block, videoUrl: e.target.value || undefined })}
    />
    <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
      <input
        type="checkbox"
        checked={!!block.isIllustrative}
        onChange={(e) => onChange({ ...block, isIllustrative: e.target.checked })}
      />
      Illustrative placeholder (hidden on production builds)
    </label>
  </div>
);

export const CalloutBlockForm: React.FC<BlockFormProps<ContentBlock & { type: "callout" }>> = ({
  block,
  onChange,
}) => (
  <div className="space-y-2">
    <select
      className={inputClass}
      value={block.variant}
      onChange={(e) => onChange({ ...block, variant: e.target.value as typeof block.variant })}
    >
      <option value="info">Info</option>
      <option value="warning">Warning</option>
      <option value="production">Production</option>
    </select>
    <input
      className={inputClass}
      placeholder="Heading (optional)"
      value={block.heading || ""}
      onChange={(e) => onChange({ ...block, heading: e.target.value })}
    />
    <textarea
      className={`${inputClass} h-20`}
      placeholder="Text"
      value={block.text}
      onChange={(e) => onChange({ ...block, text: e.target.value })}
    />
  </div>
);

export const BlockForm: React.FC<{ block: ContentBlock; onChange: (block: ContentBlock) => void }> = ({
  block,
  onChange,
}) => {
  switch (block.type) {
    case "text":
      return <TextBlockForm block={block} onChange={onChange as any} />;
    case "code":
      return <CodeBlockForm block={block} onChange={onChange as any} />;
    case "canvas2d":
      return <Canvas2DBlockForm block={block} onChange={onChange as any} />;
    case "image":
      return <ImageBlockForm block={block} onChange={onChange as any} />;
    case "slides":
      return <SlidesBlockForm block={block} onChange={onChange as any} />;
    case "flashcards":
      return <FlashcardsBlockForm block={block} onChange={onChange as any} />;
    case "mindmap":
      return <MindmapBlockForm block={block} onChange={onChange as any} />;
    case "video":
      return <VideoBlockForm block={block} onChange={onChange as any} />;
    case "quiz":
      return <QuizBlockForm block={block} onChange={onChange as any} />;
    case "callout":
      return <CalloutBlockForm block={block} onChange={onChange as any} />;
    default:
      return null;
  }
};
